import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, DrawingManager } from '@react-google-maps/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, MapPin, Pentagon, Square, CircleDashed, Trash2, Play } from 'lucide-react';
import { toast } from 'sonner';

const LIBRARIES = ['drawing', 'places'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '500px', borderRadius: '0.75rem' };
const DEFAULT_CENTER = { lat: 51.8916, lng: 11.0489 };   // Harz-ish
const DEFAULT_ZOOM = 8;

export default function MapAreaSearch({ onRunStarted }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: LIBRARIES,
    // Google removed DrawingManager from the "weekly" channel as of v3.65 (May 2026).
    // Pin to v3.62 which still contains the Drawing Library while we plan a long-term
    // migration to a custom drawing implementation.
    version: '3.62',
  });

  const mapRef = useRef(null);
  const drawnRef = useRef(null);
  const [shape, setShape] = useState(null);
  const [branche, setBranche] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const clearDrawn = useCallback(() => {
    if (drawnRef.current && drawnRef.current.setMap) {
      drawnRef.current.setMap(null);
    }
    drawnRef.current = null;
    setShape(null);
  }, []);

  const onOverlayComplete = useCallback((e) => {
    // Remove previous drawing
    if (drawnRef.current && drawnRef.current.setMap) {
      drawnRef.current.setMap(null);
    }
    drawnRef.current = e.overlay;

    if (e.type === 'circle') {
      const c = e.overlay.getCenter();
      setShape({
        type: 'circle',
        center: { lat: c.lat(), lng: c.lng() },
        radius: e.overlay.getRadius(),
      });
    } else if (e.type === 'rectangle') {
      const b = e.overlay.getBounds();
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      setShape({
        type: 'rectangle',
        bounds: { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() },
      });
    } else if (e.type === 'polygon') {
      const path = e.overlay.getPath();
      const points = [];
      for (let i = 0; i < path.getLength(); i++) {
        const p = path.getAt(i);
        points.push({ lat: p.lat(), lng: p.lng() });
      }
      setShape({ type: 'polygon', points });
    }
  }, []);

  const shapeSummary = useMemo(() => {
    if (!shape) return '';
    if (shape.type === 'circle') return `Kreis · Radius ${(shape.radius / 1000).toFixed(1)} km`;
    if (shape.type === 'rectangle') return 'Rechteck';
    if (shape.type === 'polygon') return `Polygon · ${shape.points.length} Eckpunkte`;
    return '';
  }, [shape]);

  const startSearch = async () => {
    if (!shape) { toast.error('Bitte zeichne zuerst einen Bereich auf der Karte ein.'); return; }
    if (!branche.trim()) { toast.error('Bitte gib eine Branche ein.'); return; }
    setSubmitting(true);
    try {
      const { default: api } = await import('../lib/api');
      const res = await api.post('/discovery/area-search', { shape, branche: branche.trim() });
      toast.success('Karten-Suche gestartet — Ergebnisse werden geladen...');
      if (onRunStarted && res.data?.run_id) onRunStarted(res.data.run_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suche konnte nicht gestartet werden');
    } finally {
      setSubmitting(false);
    }
  };

  if (!apiKey) {
    return <div className="p-6 rounded-xl border border-yellow-200 bg-yellow-50 text-yellow-800 text-sm">REACT_APP_GOOGLE_MAPS_API_KEY ist nicht gesetzt.</div>;
  }
  if (loadError) return <div className="p-6 text-sm text-red-600">Karte konnte nicht geladen werden.</div>;
  if (!isLoaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl border border-border bg-card">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Branche</Label>
          <Input value={branche} onChange={e => setBranche(e.target.value)} placeholder="z. B. Dachdecker, Fliesenleger" data-testid="area-branche-input" />
        </div>
        <div className="flex items-end gap-2">
          <div className="text-xs text-muted-foreground">
            <span className="block text-foreground font-medium">Auswahl:</span>
            <span data-testid="area-shape-summary">{shapeSummary || 'noch keine'}</span>
          </div>
          {shape && (
            <Button variant="outline" size="sm" onClick={clearDrawn} data-testid="area-clear-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
            </Button>
          )}
          <Button onClick={startSearch} disabled={submitting || !shape} data-testid="area-start-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" /> Suche im Bereich starten</>}
          </Button>
        </div>
      </div>

      {/* Map */}
      <div className="relative">
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={(m) => (mapRef.current = m)}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            gestureHandling: 'greedy',
          }}
        >
          <DrawingManager
            onOverlayComplete={onOverlayComplete}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: ['polygon', 'rectangle', 'circle'],
              },
              polygonOptions: { fillColor: '#10B981', fillOpacity: 0.18, strokeColor: '#10B981', strokeWeight: 2, editable: true },
              rectangleOptions: { fillColor: '#10B981', fillOpacity: 0.18, strokeColor: '#10B981', strokeWeight: 2, editable: true },
              circleOptions: { fillColor: '#10B981', fillOpacity: 0.18, strokeColor: '#10B981', strokeWeight: 2, editable: true },
            }}
          />
        </GoogleMap>

        {/* Hint overlay */}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-2 rounded-lg shadow text-xs text-muted-foreground flex items-center gap-2 pointer-events-none">
          <MapPin className="w-3.5 h-3.5 text-emerald-600" />
          <span>Zeichne ein <Pentagon className="w-3 h-3 inline" /> Polygon, <Square className="w-3 h-3 inline" /> Rechteck oder <CircleDashed className="w-3 h-3 inline" /> einen Kreis um deinen Suchbereich.</span>
        </div>
      </div>
    </div>
  );
}
